import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse, PageableApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import {
  CreateOperationProfileRequest,
  Operation,
  OperationProfile,
  OperationRequest,
  TemplatePullPreviewResponse,
  TemplatePullRequest,
  UpdateOperationProfileRequest,
} from '../models/operation-profile.model';

@Injectable({
  providedIn: 'root'
})
export class OperationProfileService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly profilesUrl = `${this.coreApiUrl}/api/v1/operation-profile`;
  private readonly operationsUrl = `${this.coreApiUrl}/api/v1/operation`;

  // Operation Profiles CRUD
  getProfiles(
    page: number,
    size: number,
    sortBy: string,
    sortDirection: string
  ): Observable<PageableApiResponse<OperationProfile>> {
    return this.http.get<PageableApiResponse<OperationProfile>>(
      `${this.profilesUrl}?page=${page}&size=${size}&sort=${sortBy},${sortDirection}`
    );
  }

  getProfileById(id: number): Observable<ApiResponse<OperationProfile>> {
    return this.http.get<ApiResponse<OperationProfile>>(`${this.profilesUrl}/${id}`);
  }

  createProfile(request: CreateOperationProfileRequest): Observable<ApiResponse<OperationProfile>> {
    return this.http.post<ApiResponse<OperationProfile>>(this.profilesUrl, request);
  }

  updateProfile(id: number, request: UpdateOperationProfileRequest): Observable<ApiResponse<OperationProfile>> {
    return this.http.put<ApiResponse<OperationProfile>>(`${this.profilesUrl}/${id}`, request);
  }

  deleteProfile(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.profilesUrl}/${id}`);
  }

  // Template pull — bring a template-derived profile up to the template's
  // current version via a three-way merge (base/theirs/mine).

  /** Preview the pull: compute the merge and report conflicts. Side-effect-free. */
  previewTemplatePull(id: number): Observable<ApiResponse<TemplatePullPreviewResponse>> {
    return this.http.get<ApiResponse<TemplatePullPreviewResponse>>(
      `${this.profilesUrl}/${id}/template-pull`
    );
  }

  /** Apply the pull: overwrite the 8 settings with the resolved values,
   *  replace operations wholesale from the template, and re-stamp the version. */
  applyTemplatePull(id: number, request: TemplatePullRequest): Observable<ApiResponse<OperationProfile>> {
    return this.http.post<ApiResponse<OperationProfile>>(
      `${this.profilesUrl}/${id}/template-pull`,
      request
    );
  }

  // Operations (scoped to profile)
  getOperationsByProfileId(profileId: number): Observable<ApiResponse<Operation[]>> {
    return this.http.get<ApiResponse<Operation[]>>(
      `${this.profilesUrl}/${profileId}/operations`
    );
  }

  createOperation(profileId: number, request: OperationRequest): Observable<ApiResponse<Operation>> {
    return this.http.post<ApiResponse<Operation>>(
      `${this.profilesUrl}/${profileId}/operations`,
      request
    );
  }

  updateOperation(id: number, request: OperationRequest): Observable<ApiResponse<Operation>> {
    return this.http.put<ApiResponse<Operation>>(`${this.operationsUrl}/${id}`, request);
  }

  deleteOperation(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.operationsUrl}/${id}`);
  }
}
